// ─────────────────────────────────────────────────────────────────────────
// src/lib/kycDocs.ts
//
// Single Source of Truth für KYC-Doc-Pflicht pro Rolle.
//
// Hintergrund (Audit 089 #8, 19.05.2026):
// Die Plattform hatte historisch widersprüchliche Annahmen darüber, welche
// Rolle welche Dokumente hochladen muss. Mit Migration 047 wurde die KYC-
// Pflicht auf Fahrer ausgeweitet, das Frontend zog aber nur teilweise nach
// (Upload-UI nur für Fahrer, Routing-Block nur für Firma → Sackgasse).
//
// Diese Datei zentralisiert die Pflicht/Optional-Definition. Alle Stellen,
// die mit KYC-Docs arbeiten (ProfilSeite, KycUserBody, AdminUsers,
// Routing in App.tsx) sollen ausschließlich über diese Helper gehen.
//
// Wenn die Pflicht/Optional-Regeln später ändern (z.B. Versicherung doch
// Pflicht für Firma), wird HIER geändert — sonst nirgends.
// ─────────────────────────────────────────────────────────────────────────

import type { Rolle, Profile, TranslateFn } from '../types';

/** Alle KYC-Dokumenten-Schlüssel, die das System kennt. */
export type DocKey = 'gewerbeschein' | 'versicherung' | 'bonitaetsnachweis';

/** Wird ein Dokument für eine Rolle gefordert? */
export type DocRequirement = 'required' | 'optional' | 'not_applicable';

/**
 * Verbindliche Pflicht/Optional-Matrix pro Rolle.
 *
 * Stand 19.05.2026 (Audit 089 #8):
 * - Fahrer: Gewerbeschein + Transportversicherung beide Pflicht (Mig. 047)
 * - Firma:  Gewerbeschein Pflicht. Versicherung seit Migration 150
 *           (23.10.2026) NICHT mehr in der Verifizierung: Fürs Bieten zählt
 *           das Zertifikat „Transportversicherung“ mit Ablaufdatum
 *           (`lib/versicherung.ts`). Reine Auftraggeber brauchen keine.
 * - Admin:  KYC nicht anwendbar
 * - Mitarbeiter (DRPX-Pro, Migration 132): KYC nicht anwendbar — es zählt
 *   die Prüfung der Firma, zu der der Zugang gehört
 */
const KYC_DOC_MATRIX: Record<Rolle, Record<DocKey, DocRequirement>> = {
  fahrer: {
    gewerbeschein: 'required',
    versicherung: 'required',
    bonitaetsnachweis: 'optional',
  },
  firma: {
    gewerbeschein: 'required',
    versicherung: 'not_applicable',
    bonitaetsnachweis: 'optional',
  },
  admin: {
    gewerbeschein: 'not_applicable',
    versicherung: 'not_applicable',
    bonitaetsnachweis: 'not_applicable',
  },
  mitarbeiter: {
    gewerbeschein: 'not_applicable',
    versicherung: 'not_applicable',
    bonitaetsnachweis: 'not_applicable',
  },
};

/** Reihenfolge, in der Docs in der UI gerendert werden sollen. */
const DOC_DISPLAY_ORDER: DocKey[] = ['gewerbeschein', 'versicherung', 'bonitaetsnachweis'];

/**
 * i18n-Schlüssel des Anzeigenamens je Dokument.
 *
 * 09.10.2026: Vorher baute `ProfilSeite.tsx` den Namen an drei Stellen
 * selbst zusammen — eine davon (roter Ablehnungs-Kasten) kannte den
 * Bonitätsnachweis nicht und zeigte dann den rohen Schlüssel.
 */
const DOC_LABEL_KEYS: Record<DocKey, string> = {
  gewerbeschein: 'gewerbescheinLabel',
  versicherung: 'versicherungLabel',
  bonitaetsnachweis: 'bonitaetsnachweisLabel',
};

/**
 * Übersetzter Anzeigename eines Dokuments (z. B. „Gewerbeschein").
 *
 * @param key Dokument-Schlüssel
 * @param t   Übersetzungsfunktion des Aufrufers
 * @returns Name in der Sprache von `t`
 */
export function getDocLabel(key: DocKey, t: TranslateFn): string {
  return t(DOC_LABEL_KEYS[key]);
}

/**
 * Welche Dokumente sind für diese Rolle Pflicht?
 * Für den Status-Übergang "alle approved" relevant.
 */
export function getRequiredDocs(rolle: Rolle): DocKey[] {
  return DOC_DISPLAY_ORDER.filter(
    (k) => KYC_DOC_MATRIX[rolle][k] === 'required',
  );
}

/**
 * Welche Dokumente sind für diese Rolle optional?
 * Werden in der UI angezeigt, aber für "approved" nicht notwendig.
 */
export function getOptionalDocs(rolle: Rolle): DocKey[] {
  return DOC_DISPLAY_ORDER.filter(
    (k) => KYC_DOC_MATRIX[rolle][k] === 'optional',
  );
}

/**
 * Alle Dokumente, die für diese Rolle relevant sind (Pflicht + Optional),
 * in Anzeige-Reihenfolge.
 */
export function getApplicableDocs(rolle: Rolle): DocKey[] {
  return DOC_DISPLAY_ORDER.filter(
    (k) => KYC_DOC_MATRIX[rolle][k] !== 'not_applicable',
  );
}

/** Ist dieses Dokument für diese Rolle Pflicht? */
export function isDocRequired(rolle: Rolle, doc: DocKey): boolean {
  return KYC_DOC_MATRIX[rolle][doc] === 'required';
}

/** Ist dieses Dokument für diese Rolle optional? */
export function isDocOptional(rolle: Rolle, doc: DocKey): boolean {
  return KYC_DOC_MATRIX[rolle][doc] === 'optional';
}

/**
 * Soll diese Rolle überhaupt KYC durchlaufen?
 * (Admin nicht.)
 */
export function roleNeedsKyc(rolle: Rolle): boolean {
  return getRequiredDocs(rolle).length > 0;
}

// ─────────────────────────────────────────────────────────────────────────
// Prüf-Bedarf eines Nutzers
//
// Hierher verschoben am 16.08.2026 aus dem früheren AdminKycPanel. Grund:
// Nachdem das eigene Verifizierungs-Menü entfallen ist, brauchen mehrere
// Stellen dieselbe Antwort auf „hat dieser Nutzer offene Prüfarbeit?" —
// die Zahl in der Menüleiste, der Filter und die Kachel in der Nutzerliste
// sowie der Warn-Punkt am Verifizierungs-Tab. Läge die Logik weiter in
// einer Admin-Seite, würden die vier Anzeigen früher oder später
// auseinanderlaufen.
// ─────────────────────────────────────────────────────────────────────────

/** Profilfeld, in dem der Dateipfad je Dokument steht. */
const KYC_FIELD_MAP: Record<DocKey, string> = {
  gewerbeschein: 'kycGewerbeschein',
  versicherung: 'kycVersicherung',
  bonitaetsnachweis: 'kycBonitaetsnachweis',
};

/**
 * Liest den hinterlegten Dateiwert eines KYC-Dokuments.
 *
 * Historisch gewachsen: Der Pfad kann im Profilfeld (`kycGewerbeschein`),
 * direkt unter dem Doc-Schlüssel oder in einem `dokumente`-Objekt liegen.
 * Alle drei Varianten kommen in Bestandsdaten vor.
 */
export function readDocFileData(user: Profile, key: DocKey): unknown {
  const profileKey = KYC_FIELD_MAP[key];
  const u = user as unknown as Record<string, unknown>;
  const fromProfileKey = profileKey ? u[profileKey] : null;
  if (fromProfileKey) return fromProfileKey;
  if (u[key]) return u[key];
  const dokumente = u['dokumente'] as Record<string, unknown> | undefined;
  if (dokumente && dokumente[key]) return dokumente[key];
  return null;
}

/**
 * Abgeleitete KYC-Zähler eines Nutzers.
 *
 * Audit 089 #8: Zähler basieren auf den für die Rolle anwendbaren Docs.
 * Bei Firma sind das nur die zwei wenn Versicherung optional, oder nur
 * Gewerbeschein bei "Versicherung nicht zutreffend" — Anzeige bleibt
 * konsistent ("1/1" statt "1/2") und die Approval-UI matched die Realität.
 *
 * `needsAttention` ist das entscheidende Maß für „hier liegt Arbeit":
 * mindestens ein HOCHGELADENES Dokument hat noch keine Bewertung. Trifft
 * zu bei Erst-Upload und bei Re-Upload (dort entfernt `uploadKycDoc` die
 * alte Bewertung automatisch).
 *
 * Bewusst NICHT dasselbe wie `kycStatus === 'pending'`: Diesen Status trägt
 * auch jeder frisch registrierte Nutzer, der noch gar nichts hochgeladen
 * hat. Eine Zahl, die solche Nutzer mitzählt, zeigt Arbeit an, wo keine
 * ist — und wird dadurch wertlos.
 */
export function getKycCounts(user: Profile): {
  applicableDocs: DocKey[];
  approvedCount: number;
  uploadedCount: number;
  needsAttention: boolean;
} {
  const reviews = (user.dokReviews || {}) as Record<
    string,
    { status?: string } | undefined
  >;
  const applicableDocs = getApplicableDocs(user.rolle);
  const approvedCount = applicableDocs.filter((k) => {
    const r = reviews[k];
    return r && r.status === 'gueltig';
  }).length;
  const uploadedCount = applicableDocs.filter(
    (k) => !!readDocFileData(user, k),
  ).length;
  const needsAttention =
    applicableDocs.some((k) => {
      const hasFile = !!readDocFileData(user, k);
      const hasReview = !!reviews[k];
      return hasFile && !hasReview;
    }) && user.kycStatus !== 'approved';
  return { applicableDocs, approvedCount, uploadedCount, needsAttention };
}

/** Wie viele Nicht-Admin-Nutzer haben offene Prüfarbeit? */
export function countKycNeedsAttention(users: Profile[]): number {
  return users.filter((u) => u.rolle !== 'admin' && getKycCounts(u).needsAttention)
    .length;
}
